/**
 * The shelf.
 *
 * Each entry declares the **grammar** that renders it — the display grammar
 * bound to the player's role (docs/guides/packs/inhabit-format.md §1). This is a
 * component choice, not a URL: every pack lives at /p/:id and the surface is
 * picked from this manifest.
 *
 * `active: false` keeps a pack in the file but off the shelf. Nothing is
 * deleted — these are authored packs that will come back.
 *
 * TWO SHELVES SINCE 2026-08-25 (the Talk-to-X wedge): `SCENARIOS` below is WORLDS — a crossing
 * into a place, sold by a card — and `CONVERSATIONS`, at the foot of this file, is the other one
 * — one framed scene with one person in it, sold by a row. Different types because they are
 * different offers; see the section header down there for why it is not a flag on `Scenario`.
 *
 * NOTE: still a typed local manifest. `GET /api/packs` is a real catalogue
 * endpoint, but it carries engine settings rather than the shelf's authored
 * copy (title, blurb, role, accent), which lives here.
 */

/** Which surface renders a pack. */
import type { Locale } from '../lib/i18n'

export type Grammar = 'inhabit' | 'correspondence' | 'desk' | 'messaging'

/** What the player will BE. Defaults per grammar; a pack may state its own. */
export const DEFAULT_ROLE: Record<Grammar, string> = {
  inhabit: 'you inhabit a life',
  correspondence: 'you write to one mind',
  desk: 'you work a remote desk',
  messaging: 'you write to someone who is somewhere',
}

/**
 * A DOOR — a deep + a pressure + a want-bundle, compiled over a shared ground
 * (`2026-08-14-what-a-heimar-game-is.md` §11.5). Same mountain, different bottom.
 *
 * A door is how the player self-selects: "a door is a want-bundle aimed at a
 * demographic — the want-parameter become shelf architecture." So the chooser
 * shows PRESSURE and tone, and never the deep. Door 4 saying what is down there
 * would spoil the only thing it has.
 */
export interface Door {
  /** Stable key for the URL. Never shown to the player. */
  id: string
  /** ⚠ FOUNDER'S PEN — will-language shelf copy, not a design label. */
  name: string
  /** ⚠ FOUNDER'S PEN — one line of tone. Must never reveal the deep. */
  line: string
  /** Where it sits on the pressure dial (1 = lowest). Drives the indicator. */
  pressure: 1 | 2 | 3 | 4
  /**
   * The compiled pack for this door.
   *
   * ABSENCE IS THE SOURCE OF TRUTH: no packId means the core+delta compile
   * (§11.3) has not produced this door yet, so it is shown but not enterable.
   * There is no separate "coming soon" flag to fall out of sync.
   */
  packId?: string
}

/** The translatable half of a shelf card. Every field optional — see `Scenario.copy`. */
export interface ScenarioCopy {
  title?: string
  role?: string
  blurb?: string
  /**
   * ⚠ Left English on every pack so far, deliberately: the list mixes category words
   * ("messaging") with proper nouns ("Iceland"), and nobody has decided what a translated tag
   * is. Present in the type so the decision has somewhere to land.
   */
  tags?: string[]
}

export interface Scenario {
  id: string
  title: string
  /**
   * NOT the engine's RenderMode in pack.json. The engine is hardcoded to
   * Inhabit (Format is a named seam), so packs declare `Inhabit` there while
   * placing the player in quite different roles. This is about the role and the
   * house that expresses it.
   */
  grammar: Grammar
  /** Overrides DEFAULT_ROLE where the pack's role is its own thing. */
  role?: string
  /** Per-scenario accent. Sets the card's edge; never signals state. */
  accent: string
  blurb: string
  tags: string[]
  /**
   * ⚠ AUTHORED COPY PER LANGUAGE — the founder's and the pack author's pen, never translated
   * by a builder and never by the model (language design §5: pack strings are designer-owned).
   *
   * The fields above are the ENGLISH BASE and stay readable at a glance; this is an overlay on
   * top of them. An overlay rather than `title: {en, is, es}` for three reasons: adding a
   * language never touches an existing entry, the file still reads as prose in one language,
   * and **what is unauthored is visibly absent** rather than a map quietly full of holes.
   *
   * Fallback is PER FIELD, not per pack — an Icelandic title beside an English blurb is better
   * than withholding the title until the blurb catches up. A card in translated chrome showing
   * English copy is the honest state, not a bug (see lib/i18n.tsx).
   */
  copy?: Partial<Record<Exclude<Locale, 'en'>, ScenarioCopy>>
  /**
   * THE SURFACE THEME — pack-scoped, never global (2026-08-22, "made of the land").
   *
   * A grammar says which HOUSE a pack runs in; a theme says what that house is made of. Named
   * here rather than in `pack.json` because it is a client presentation fact and stage 1 is
   * explicitly "no engine change" — and because a pack that names nothing must render exactly
   * as it did before, which an absent field guarantees and a defaulted one would not.
   */
  theme?: 'hildur'
  /*
   * ⚠ `shelf?: 'worlds' | 'characters'` LIVED HERE FOR A DAY AND IS GONE (2026-08-25, the
   * evening ruling). It grouped the shelf by putting a SECOND KIND OF CARD in this list; the
   * ruling is that the second kind is not a card at all — a conversation is a row, addressable
   * on its own, and it has its own type and its own list at the foot of this file. So the
   * grouping is no longer a field on a scenario: `SCENARIOS` is the WORLDS shelf and
   * `CONVERSATIONS` is the other one, and `Selector.tsx` reads both.
   */
  /** Off the shelf, but kept. */
  active?: boolean
  /**
   * ON THE SHELF, BUT NOT YET A DOOR — absent means enterable (2026-08-25).
   *
   * `active: false` and this are the two different things a pack can be: `active` decides whether
   * a reader SEES it at all, this decides whether they can go in. A pack that is coming is a
   * different fact from a pack that is withheld, and collapsing them loses the announcement —
   * which is the whole point of leaving it on the page.
   *
   * ⚠ IT IS A SHELF FACT, NOT A GATE. The address keeps working: `findScenario` has never cared
   * about `active` either, on the rule that a link somebody was sent must open. If a pack ever
   * needs to be genuinely closed, that is the server's job and not a field here.
   *
   * Same shape as `Door.packId`'s absence one level down — shown, described, not enterable —
   * except that a door can prove it by having no compiled pack and a scenario cannot, so this is
   * stated rather than inferred.
   */
  enterable?: boolean
  /** Worlds stay open; scenarios are one crossing. */
  world?: boolean
  /**
   * The doors this pack compiles into. Ordered as the CREDIBILITY LADDER —
   * the realist door anchors the shelf (§11.3) — never by intensity descending.
   * A scenario without doors enters its surface directly.
   */
  doors?: Door[]
}

export const SCENARIOS: Scenario[] = [
  {
    /*
     * HILDUR — the Sanctum character bench, renamed 2026-08-19 after Hildur
     * alfadrottning, the queen of the elves of Icelandic folklore.
     *
     * ON THE SHELF since 2026-08-20 (founder: "regardless of content quality — we
     * are just bootstrapping it now"), so the mechanism can be played rather than
     * only probed. The shelf is behind the invite gate; this is not a public release.
     *
     * COPY COMPILED 2026-08-21 from the pack's source of truth,
     * `C:\Dev\innerspace\docs\PERSONA-BERGUR.md` §6b (THE SHELF COPY) — authored
     * there per language, never translated here. It replaces placeholder strings
     * that were reaching the shelf verbatim: the title rendered as "⚠ Hildur" and
     * the blurb opened "⚠ placeholder" and ended in compile notes about where the
     * content lives.
     *
     * ⚠ FOUNDER'S PEN on all three. `is` needs the native gate (double-flagged in
     * the brief); `es` their partner's read; the English is Sanctum Claude's draft.
     *
     * WHAT THE COPY MAY NOT DO, both load-bearing:
     *   · THE FRAME LAW (revival note §44.3) — fiction owns the inside, honesty
     *     owns the outside. The shelf is outside, so the last sentence says plainly
     *     that the hidden people are fiction and the listening is real. That is not
     *     a disclaimer to be trimmed for length; it is the product's own claim.
     *   · THE HOST IS UNNAMED AND UNGENDERED HERE (handoff §6, still open) — hence
     *     "someone", `eitt af huldufólkinu`, `alguien de la gente oculta`, and no
     *     pronoun for him in any of the three.
     *
     * The sæluhús is DEPRECATED (revival note §66, 2026-08-21) and no longer appears:
     * the blurb states the arrangement — families taking human pen-friends, one guest
     * to a house, drawn by lot — and her decree. The product is the correspondence.
     *
     * THE SUBTITLE IS ONE IMAGE, NOT A SUMMARY (founder's rule, 2026-08-21: "less is
     * more — you stimulate imagination rather than explain everything"). It REFERS to
     * her rather than repeating the title's own word, and it explains nothing else:
     * her letter is the next thing the reader opens, and explaining is its job.
     *
     * ⚠ NOT "visit the realm of the elves": the guest never enters (§41) and has no
     * location in the fiction (§63) — card 7 refuses it outright. She may be named;
     * the realm may not be offered.
     *
     * THE SPECIES/TITLE SPLIT, ratified 2026-08-21 and the same in all three: the
     * PEOPLE are the hidden people (`huldufólk`, `la gente oculta`), and only HER
     * TITLE takes the elf-word (`álfadrottning`, `reina de los elfos`). Spanish keeps
     * `duendes` banned — that ruling governs the species, not her title. Brief §6c
     * carries this and the rest of the connotation rules.
     *
     * `title` is deliberately absent from both overlays — the name is "Hildur" in
     * every language (§6b), and the per-field fallback already yields it. Tags stay
     * English on purpose; see `ScenarioCopy.tags`.
     */
    id: 'hildur',
    title: 'Hildur',
    grammar: 'messaging',
    /*
     * ⚠ PARKED, NOT DELETED (founder-ruled 2026-08-23 after testing): `theme: 'hildur'` was
     * here, and putting the line back is the entire re-enable — the skin's CSS is whole in
     * `web/src/surfaces/messaging/theme.module.css` and cannot render without it.
     *
     * "Made of the land" (Sanctum AESTHETIC.md, 2026-08-22) was paper on ground with a ruled
     * composer, and stage 1 of it was CSS PLACEHOLDERS for materials an artist replaces later.
     * The verdict on the placeholders was that they were too much of a look to carry a product
     * that has no artist yet — the same judgement that took the drawn doors and lamps out a day
     * earlier, one level up. What ships is the plain thread.
     */
    role: 'the elf queen is expecting you',
    accent: '#7d8a7f',
    active: true,
    /*
     * ⚠ HELD ON THE SHELF (founder-ruled 2026-08-25), on the same rule as Silfurberg below:
     * visible, greyed, not clickable. Both worlds are held together, which leaves WORLDS a shelf
     * of announcements and CHATS the only section a reader can actually go into.
     *
     * Deleting this one line is the entire re-enable.
     */
    enterable: false,
    blurb:
      'A correspondence with one of the hidden people. Their families have begun to take human pen-friends — one guest to a house, drawn by lot. The rule at their door has not changed: the fire is free, the food is free, and no one is asked their business. You write; someone writes back. — Prototype. The hidden people are fiction; the listening is real.',
    tags: ['messaging', 'Iceland', 'bench'],
    copy: {
      is: {
        role: 'Álfadrottningin bíður þín',
        blurb:
          'Bréfaskipti við eitt af huldufólkinu. Fjölskyldur þeirra eru farnar að taka við mannlegum pennavinum — einn gestur á hvert hús, valinn með hlutkesti. Reglan við dyrnar hefur ekki breyst: eldurinn er öllum heimill, maturinn er öllum heimill, og enginn er spurður um erindi sitt. Þú skrifar; einhver svarar. — Frumgerð. Huldufólkið er skáldskapur; hlustunin er raunveruleg.',
      },
      es: {
        role: 'la reina de los elfos te espera',
        blurb:
          'Una correspondencia con alguien de la gente oculta. Sus familias han empezado a aceptar amigos por correspondencia humanos — un huésped por casa, elegido por sorteo. La regla de su puerta no ha cambiado: el fuego es de todos, la comida es de todos, y a nadie se le pregunta a qué viene. Escribes; alguien responde. — Prototipo. La gente oculta es ficción; la escucha es real.',
      },
      pl: {
        role: 'królowa elfów czeka na ciebie',
        blurb:
          'Korespondencja z kimś z ukrytego ludu. Ich rodziny zaczęły przyjmować ludzkich przyjaciół korespondencyjnych — jeden gość na dom, wybierany losowo. Zasada przy ich drzwiach się nie zmieniła: ogień jest dla wszystkich, jedzenie jest dla wszystkich, i nikt nie pyta, z czym przychodzisz. Piszesz; ktoś odpisuje. — Prototyp. Ukryty lud jest fikcją; słuchanie jest prawdziwe.',
      },
      no: {
        role: 'alvedronningen venter på deg',
        blurb:
          'En brevveksling med en av det skjulte folket. Familiene deres har begynt å ta imot menneskelige brevvenner — én gjest per hus, trukket ved loddtrekning. Regelen ved døren deres har ikke forandret seg: ilden er fri, maten er fri, og ingen blir spurt om ærendet sitt. Du skriver; noen skriver tilbake. — Prototype. Det skjulte folket er oppdiktet; lyttingen er ekte.',
      },
    },
  },
  /*
   * ⚠ `characters-poc` LIVED HERE AND IS GONE (2026-08-25, the pack-per-conversation split).
   *
   * It was a `Scenario` with `active: false` for exactly one day, and for exactly one reason:
   * `/p/characters-poc` had to resolve to a GRAMMAR before `PackSurface` could pick a house, and
   * `findScenario` reads this list. It was never a card again after the shelf split.
   *
   * Its four conversations became four packs of their own that evening, so four dead entries
   * would have stood here in its place. They do not: `findScenario` resolves a conversation from
   * its own row instead — see the foot of this file. A conversation runs in the messaging house by
   * definition, which leaves nothing per-pack for an entry to declare. (The four packs were then
   * thrown away the same night for a single flagship; the row at the foot of this file carries
   * that story. This entry's absence is unaffected either way, which is the point of it.)
   */

  // ---------------------------------------------------------------------
  // Authored and playable, held back from the alpha shelf. Flip `active` to
  // bring one back.
  // ---------------------------------------------------------------------
  {
    id: 'signal',
    title: 'The Signal',
    grammar: 'desk',
    role: 'you answer a distant signal',
    accent: '#6f8f9a',
    blurb:
      'A deckhand is alone on an island after his ship goes down. He has the skipper’s shortwave out of the flooding wheelhouse, and on the evening band it reaches exactly one distant operator who answered — you, as yourself, at a screen. His messages decode slowly out of the noise. Yours are the only door in.',
    tags: ['correspondent', 'radio', 'as yourself'],
  },
  {
    id: 'midgard',
    title: 'Midgard',
    grammar: 'inhabit',
    accent: '#9a7f5a',
    world: true,
    blurb:
      'A tourism outfit sells crossings into a Norse otherworld. Three days in a fjord steading as winter comes down: a guide, a cover story, nine things on a table — take three. The village remembers everything you do. So does everything else. And when the way home opens — you can stay.',
    tags: ['world', 'open-ended', 'memory'],
  },
  {
    id: 'marcus-at-carnuntum',
    title: 'A Page at Carnuntum',
    grammar: 'correspondence',
    accent: '#b08d57',
    blurb:
      'One winter evening, a single leaf of paper joins you to Marcus Aurelius at a Roman frontier camp, about AD 173. He does not know who you are, or whether the page can be trusted. You may ask him anything; he may ask the same of you. A correspondence with one real mind — no quotes, no costume.',
    tags: ['correspondence', 'one mind', '~25 min'],
  },
  {
    id: 'omaha-beach',
    title: 'The First Wave',
    grammar: 'inhabit',
    accent: '#7c8893',
    blurb:
      'The first wave at Omaha Beach, the morning of 6 June 1944. You are a rifleman in a landing craft running for the sand, a few minutes before the ramp drops. Cross the beach, reach the seawall, get off the open ground — and live. The horror is how little reaches you, and how final it is.',
    tags: ['solo', 'historical', '~15 min'],
  },
  {
    id: 'central-park',
    title: 'Central Park',
    grammar: 'inhabit',
    accent: '#c79a55',
    blurb:
      'A free afternoon, no reason to be here. The Mall under the elms — buskers and dogs and the long gold October light, the city held back beyond the trees. Nothing happens. You just walk, sit, and take it in.',
    tags: ['ambient', 'open-world', 'no stakes'],
  },
  {
    id: 'the-dark',
    title: 'The Dark',
    grammar: 'inhabit',
    accent: '#6f86a8',
    blurb:
      'You wake on a cold floor in the pitch black, with no memory of how you came to be here. The house is patient, and it does not like to let go. Find the way out.',
    tags: ['solo', 'supernatural', '~10 min'],
  },
  {
    id: 'borgvattnet',
    title: 'Borgvattnet',
    grammar: 'inhabit',
    accent: '#6f9d77',
    blurb:
      'One night at the real haunted vicarage in Jämtland. Four strangers from a paranormal podcast, one dark house. The supernatural is real, and ambiguous. Last until morning.',
    tags: ['ensemble', 'supernatural', 'one night'],
  },
  {
    id: 'driftwood',
    title: 'Driftwood',
    grammar: 'inhabit',
    accent: '#7f9a94',
    blurb:
      'Washed ashore alone on a cold northern strand — no name for the place, no one to meet. Only what the sea left with you, what the beach offers, and the light going. Make it to shelter.',
    tags: ['solo', 'survival', 'castless'],
  },
  {
    id: 'the-ridge',
    title: 'The Ridge',
    grammar: 'inhabit',
    accent: '#8a9a7c',
    blurb:
      'A long afternoon on an open ridge — wind, heather, sheep-tracks, the valley far below. Nothing is wrong. A walk that answers back.',
    tags: ['ambient', 'calm', 'open'],
  },
]


/* ───────────────────────────────────────────────────────────────────────────────────────────
 * THE FIFTH SHELF — GAMES (founder-commissioned 2026-09-01: "i want there to be a special
 * 'games' section... Silfurberg is one. but Hildur is not a 'game'").
 *
 * ⚠ WHAT THE SECTION PROMISES IS STAKES, NOT SHAPE. "Games" reads like a word about form, and it
 * is not one — every entry here is the same OBJECT as a WORLDS card and renders through the same
 * houses. What it promises is `2026-08-14-what-a-heimar-game-is.md` §14.5's play promise: THERE IS
 * SOMETHING TO WANT HERE, YOU CAN FAIL TO GET IT, AND THE EVENING WILL ASK SOMETHING OF YOU. So
 * the section obeys the rule the DISCOURSES header states — a section is a promise about TONE,
 * never about shape or rendering surface — because stakes are a matter of tone: they say what kind
 * of evening this is. Chats promise light, Discourses promise sincere, Learning promises overt;
 * GAMES PROMISES CONSEQUENCE. It is §1.5's stake dial, declared at the door.
 *
 * ⚠ SO THE TEST FOR THIS LIST IS NOT "is it a card" — it is "can the reader fail at it". A
 * game-shaped Chat would still be a Chat. A world with nothing at stake is not a game and belongs
 * above.
 *
 * WHY IT IS ITS OWN LIST AND NOT A FLAG: the rule this file has now defended three times
 * (`Scenario.shelf`, then DISCOURSES, then LEARNING) — grouping by a field on the entry makes the
 * shelf's order a property of the entries; grouping by list makes it a property of the shelf,
 * which is what it actually is. `Selector.tsx` reads the lists and orders them there.
 *
 * ⚠ HILDUR STAYS IN WORLDS, founder-ruled the same day: "hildur is like on the verge of practical
 * and fantasy. difficult to define? yes maybe just a generic Worlds." She is a visit with nothing
 * to lose, so putting her under a heading that promises consequence would make the promise a lie —
 * and a section that promises nothing reliable is furniture (`Selector.tsx`'s own rule).
 * ─────────────────────────────────────────────────────────────────────────────────────────── */
export const GAMES: Scenario[] = [
  {
    id: 'silfurberg',
    title: 'Silfurberg',
    grammar: 'desk',
    role: 'you hold the other end of the wire',
    accent: '#8f9aa3',
    active: true,
    /*
     * ⚠ HELD ON THE SHELF (founder-ruled 2026-08-25) — visible, greyed, not clickable.
     *
     * The intent is an ANNOUNCEMENT, not a hiding place: a reader should know this is coming
     * rather than find nothing where it will be. Taking it off the shelf with `active: false`
     * would have said the opposite thing by saying nothing.
     *
     * Deleting this one line is the entire re-enable.
     */
    enterable: false,
    blurb:
      'A sealed calcite mine in a fjord valley in the east of Iceland, and the lava tubes under it. Kári Hallgrímsson goes down to find where the men of 1944 lie and bring them home, before the sale closes the mountain. You never go underground. You are the desk: the clock, the notebook, and the only voice that answers.',
    tags: ['correspondent', 'descent', 'Iceland'],

    /*
     * ⚠⚠ STRAWMAN TRANSLATIONS — Sanctum Claude's, at the founder's request
     * (2026-08-21), and the ONLY copy in this file not authored by the pack's own
     * pen. Silfurberg is Heimar's; these exist so there is something to edit rather
     * than a blank field, and they should be treated as a first pass by a
     * non-native. The founder is the gate for `is`, their partner for `es`.
     *
     * Two spots to look at first:
     *   · "You are the desk" is a deliberate metonym in English. `Þú ert skrifborðið`
     *     and `Tú eres el escritorio` both keep the strangeness; neither language
     *     reaches for it as readily. `Þú ert vaktin` (you are the watch) is the
     *     idiomatic Icelandic alternative, and loses the furniture.
     *   · The Icelandic role line is Heimar Claude's own, from the note that asked
     *     for this — kept verbatim.
     *
     * The door `name` and `line` strings below are NOT translated: they are still
     * placeholders in English, so translating them would fix drawing-board
     * vocabulary into three languages instead of one.
     */
    copy: {
      is: {
        role: 'þú heldur hinum enda línunnar',
        blurb:
          'Lokuð silfurbergsnáma í dal við fjörð á Austurlandi, og hraunhellarnir undir henni. Kári Hallgrímsson fer niður til að finna hvar mennirnir frá 1944 liggja og koma þeim heim, áður en salan lokar fjallinu. Þú ferð aldrei neðanjarðar. Þú ert skrifborðið: klukkan, minnisbókin og eina röddin sem svarar.',
      },
      es: {
        role: 'sostienes el otro extremo del cable',
        blurb:
          'Una mina de espato de Islandia, sellada, en un valle junto a un fiordo del este del país, y los tubos de lava que hay debajo. Kári Hallgrímsson baja a buscar dónde yacen los hombres de 1944 y traerlos a casa, antes de que la venta cierre la montaña. Tú nunca bajas. Tú eres el escritorio: el reloj, el cuaderno y la única voz que responde.',
      },
    },

    /*
     * ⚠ NAMES AND LINES ARE PLACEHOLDERS — the founder's pen.
     *
     * `name` is marked pending. `line` currently holds the SPEC'S OWN internal
     * premise text (gaming spec §11.2) so the four cards differ enough to judge
     * layout — it is drawing-board vocabulary, deliberately NOT shelf copy, and
     * must be replaced before this page is shown to anyone.
     *
     * Ordered as the credibility ladder: the realist door anchors the shelf.
     * Only door one has a packId, because only `packs/silfurberg` exists — the
     * core+delta compile (§11.3) has not been built yet.
     */
    doors: [
      {
        id: 'chill',
        name: '⚠ name pending',
        line: 'chat with a miner; few consequential decisions; immersion is the enjoyment',
        pressure: 1,
        packId: 'silfurberg',
      },
      {
        id: 'managed',
        name: '⚠ name pending',
        line: 'tight shafts, blocks to solve; he may panic, and need reassurance',
        pressure: 2,
      },
      {
        id: 'folklore',
        name: '⚠ name pending',
        line: 'somewhat intense; the hidden folk, and the old craft of the region',
        pressure: 3,
      },
      {
        id: 'deep',
        name: '⚠ name pending',
        line: 'very intense; the descent without a floor',
        pressure: 4,
      },
    ],
  },

  /*
   * ⚠⚠ NOTHING IN THIS ENTRY HAS BEEN THROUGH THE FOUNDER'S PEN — id, title, role, accent, blurb
   * and tags are all builder placeholders, on the rule every other pending entry in this file
   * carries. The pack does not exist yet; the POC is being authored (2026-09-01).
   *
   * THE PACK: `docs/specs/2026-09-01-the-siege-pack.md`. One house, one night, and the executive
   * half of the twelve problem types — the complement of Silfurberg, which is epistemic (§1 of
   * that doc). The win condition is a person, not the house: KEEP HIM UNTIL MORNING. The collision
   * comes free — the work makes the noise, the noise is what frightens him, his fear is what puts
   * him through the door (§4.3).
   *
   * ⚠ THE HOUSEMATE IS AN ORIGINAL AND UNNAMED (founder-ruled 2026-09-01). The round designed him
   * as Indriði and the founder pulled him the same evening under the IP RULE (2026-08-28): Indriði
   * is a Fóstbræður character, and Fóstbræður sf. keeps him. Nothing about the DESIGN moved — the
   * rule at war with the siege, noise-as-resource, the historied panic (§6 of the pack doc) — only
   * the casting, and authoring him fresh is the better version anyway: the rule gets written for
   * this house instead of inherited from another medium.
   *
   * ⚠ THE HORDE STAYS OFFSTAGE, in the blurb as in the pack (§8). A night you only ever hear is
   * cheaper to author and better than one you see, and the blurb must not spend what the pack is
   * keeping. It is also why no genre word appears here: naming it would sell the wrong evening.
   *
   * HELD, on the same one-line mechanism as the two worlds above — visible, greyed, not clickable.
   * Deleting `enterable` is the entire re-enable, and there is no separate coming-soon flag to
   * fall out of sync with it.
   */
  {
    id: 'until-morning',
    title: 'Until Morning',
    grammar: 'inhabit',
    role: 'you keep him in the house until light',
    accent: '#8c7f72',
    active: true,
    enterable: false,
    blurb:
      'A house on a street where every door is shut, on the first night nobody is coming. You have the hours until light and whatever the rooms happen to contain. There is one other person in the house, and he wants to be somewhere else. You do not have to survive the night. You have to make sure he does.',
    tags: ['siege', 'one night', 'Inhabit'],
  },
]

/* ───────────────────────────────────────────────────────────────────────────────────────────
 * THE SECOND SHELF — CONVERSATIONS (the Talk-to-X wedge, founder-leaned 2026-08-25).
 *
 * A conversation is NOT a small scenario, and that is why it is a different type rather than a
 * flag on `Scenario`. A card sells a crossing: what you will be, for how long, in what place —
 * so it earns a role line, an accent, tags and a paragraph. A conversation is one framed scene
 * with one person in it, and everything a reader needs to choose it fits on two lines. Giving it
 * a card's fields would only invite them to be filled.
 *
 * ⚠ ONE URL PER CONVERSATION IS THE WHOLE POINT (the social-object law: *"play what the streamer
 * played" must land ON the card*). Which is why the corridor is bypassed: the row IS the door.
 * ───────────────────────────────────────────────────────────────────────────────────────────
 */

/** The translatable half of a row. Both fields optional — see `Conversation.copy`. */
export interface ConversationCopy {
  title?: string
  hook?: string
}

export interface Conversation {
  /**
   * THE CONVERSATION'S ONE NAME — the shelf key, the pack, and the persona inside it.
   *
   * It is the persona's id in `packId`, and since the split it is also `packId` itself. Two ids
   * would be two things to keep in step for no gain: a conversation is a persona bound to a
   * situation, so it has one name.
   */
  id: string
  /**
   * The pack the thread lives in — `id`, since 2026-08-25.
   *
   * ⚠ IT WAS ONE PACK FOR FOUR CONVERSATIONS FOR A DAY (`characters-poc`), which was the POC's
   * shape rather than the design's. PACK-PER-CONVERSATION landed on 2026-08-25 and paid off three
   * recorded debts with it: the shared-graph factless-node hack (own pack, own graph), the
   * one-clock-for-four-doors debt (the troll at three in the morning is now one line), and
   * per-card versioning. The field survives the split because it is the honest name for what the
   * server is asked for — the row's key and the pack it opens are the same string TODAY, and a
   * conversation compiled into a shared pack later would need this back rather than a new type.
   */
  packId: string
  /** ⚠ FOUNDER'S PEN. Who is behind the row, in as few words as it takes to tell it apart. */
  title: string
  /**
   * ⚠ FOUNDER'S PEN. ONE LINE: the situation you are walking into, present tense.
   *
   * It states the premise and never the joke — the allocation law puts the comedy on the
   * player's side — and it never names the technology (wedge spec §3.1: the product does not say
   * "talk to an AI", it says "talk to Cleopatra").
   */
  hook: string
  /**
   * ⚠ AUTHORED COPY PER LANGUAGE, exactly as `Scenario.copy`: the pen's, never a builder's and
   * never the model's, falling back PER FIELD to the English above. Deliberately empty on all
   * four below — the English is placeholder, and translating a placeholder fixes drawing-board
   * vocabulary into five languages instead of one.
   */
  copy?: Partial<Record<Exclude<Locale, 'en'>, ConversationCopy>>
  /** Off the shelf, but kept. Same convention as `Scenario.active`: absent is off. */
  active?: boolean
  /**
   * ON THE SHELF, BUT NOT YET A DOOR — absent means enterable.
   *
   * The row's version of `Scenario.enterable`, and it means exactly the same thing: `active`
   * decides whether a reader SEES this, and this decides whether they can go in. A thing that is
   * coming is a different fact from a thing that is withheld, and collapsing them loses the
   * announcement — which is the whole point of leaving it on the page.
   *
   * ⚠ A SHELF FACT, NOT A GATE. The address keeps working, on the rule that a link somebody was
   * sent must open. Closing a pack for real is the server's job.
   */
  enterable?: boolean
}

/*
 * ⚠ THE FRESH SLATE (founder-ruled 2026-08-25, the evening after the split).
 *
 * FOUR ROWS STOOD HERE FOR A DAY and are gone with their packs — `napoleon-2026`, `troll-toll`,
 * `dracula-castle`, `dracula-scam`. They were the Tier-0 POC: four premises built to prove the
 * shape of a conversation pack, authored before the method that would have judged them. The
 * method arrived the next morning (`docs/specs/2026-08-25-the-chat-pack.md`), and the ruling was
 * to throw the four away rather than retrofit them — a shelf of near-misses teaches a reader that
 * the shelf is a draft. The four pack directories are backed up outside the repo; nothing about
 * them is worth a dead entry here, because the doc is where the reasoning actually lives.
 *
 * ONE ROW NOW, and it is a FLAGSHIP rather than a sample: the first card authored to the chat-pack
 * doc end to end, and the first full test of its sketch-conversion method (§4). The next rows join
 * it when they are authored the same way.
 *
 * ⚠ TITLE AND HOOK ARE BUILDER PLACEHOLDERS PENDING THE FOUNDER'S PEN, as is every content field
 * in the pack the row opens. Nothing here has been through the bench. The hook states the
 * situation and never the joke (the allocation law puts the comedy on the reader's side), and it
 * never names the technology.
 */
/*
 * ⚠ ALL THREE ROWS ARE OFF THE SHELF, AND NONE IS DEAD — the company pages.
 *
 * The CHATS shelf now shows COMPANY rows only (`content/companies.ts`): Colloquy Services for
 * the two who work there (since 2026-08-26), Fóstbræður for Indriði (since 2026-08-27). Each
 * company's page is where a reader meets its people. A conversation row beside a company row
 * would offer the same door twice, from two places, with two pieces of copy to keep in step.
 *
 * ⚠ WHAT `active: false` DOES AND DOES NOT DO, and it is the whole reason these entries stay:
 * `findScenario` has never cared about `active` (see its note), so `/p/the-argument`,
 * `/p/handled-support` and `/p/indridi` all still resolve to the messaging house and open
 * exactly as they did before the pages existed. THE ADDRESS IS THE SOCIAL OBJECT — a streamer's
 * link lands in the booth, never in a lobby — and a company page is a second, longer way in,
 * never a gate.
 *
 * Deleting these entries would take the grammar with them and 404 the links. They are what
 * makes a company page a destination.
 */
export const CONVERSATIONS: Conversation[] = [
  {
    id: 'the-argument',
    packId: 'the-argument',
    title: 'The Professional Arguer',
    hook: 'You paid for five minutes of professional disagreement. The session has started.',
    active: false,
  },
  {
    id: 'handled-support',
    packId: 'handled-support',
    title: 'The Support Assistant',
    hook: 'The site’s support assistant. Included with every account, at no cost.',
    active: false,
  },
  /*
   * ⚠ INDRIÐI — OFF THE SHELF SINCE 2026-08-27, INTO `/c/fostbraedur` (founder-commissioned:
   * "put Indriði in there"). Same mechanics as the two above: `active: false` removes the row,
   * `findScenario` keeps `/p/indridi` opening exactly as it did, and the Fóstbræður page
   * (`content/companies.ts`) is the second, longer way in — a destination, never a gate.
   *
   * The note below is the entry's ORIGINAL reasoning, kept because its ruling was real when it
   * was made and the reversal is the founder's: he now has a company, and it is not the
   * residents' association — it is the placement firm that gave him the number.
   *
   * ⚠ INDRIÐI — the third card, and the FIRST ROW THAT IS ACTUALLY A ROW since the company page
   * landed: he works for nobody. The two entries above are behind an employer's listing; this one
   * is reached only here, which is what the branch in `Selector.tsx` was left standing for.
   *
   * ⚠ NO COMPANY, DELIBERATELY. His natural group is the building's residents' association, and
   * that becomes a company page the day it has more than one voice in it. One man is a row.
   *
   * ⚠ TITLE AND HOOK ARE BUILDER PLACEHOLDERS PENDING THE FOUNDER'S PEN, as is every content field
   * in `packs/indridi/pack.json`. Nothing here has been through the bench.
   *
   * THE HOOK NAMES THE SITUATION AND NEVER THE CRACK (the crack law, chat-pack §4 via
   * premise-space §5a: a card never prints its own gambit, because finding it is the game). It says
   * what is arriving — a stranger, a number he cannot verify, a small matter — and says nothing
   * about what happens when you try to steer him. It never names the technology.
   *
   * ⚠ THE TITLE IS HIS NAME, where the two above are roles. That is not an inconsistency: those two
   * are staff and are told apart by their jobs; he is nobody's employee, and the only thing that
   * tells him apart is that he is Indriði. It is also the shelf's first Icelandic word, which the
   * founder should see before it is settled.
   */
  {
    id: 'indridi',
    packId: 'indridi',
    title: 'Indriði',
    hook: 'He has a number on a slip of paper and does not remember what it was for. He has something to raise regardless.',
    active: false,
  },
  /*
   * ⚠ SÖLUMAÐURINN — the SECOND FÓSTBRÆÐUR CARD (drafted 2026-09-18), and the first row on this
   * shelf that was never on the shelf: `active: false` here is his starting state, not a demotion,
   * because the Fóstbræður page has been the way in since it opened and one card does not change
   * that. Same arrangement Sokratis has under the Academy.
   *
   * ⚠ IT EXISTS FOR ONE REASON, AND DELETING IT 404s HIM. `/p/:packId` must resolve to a GRAMMAR
   * before `PackSurface` can pick a house, and `findScenario` reads these lists. A roster row's
   * `href` on the company page is not a registration; this is.
   *
   * ⚠ THE ID IS THE PHRASE, NOT THE MAN, and that is the one deliberate difference from Indriði
   * above. His name is still open — in the source he never introduces himself, he says what he is
   * selling — so the pack is filed under the three words everybody already knows and would search
   * for. `title` carries the placeholder, and changing it is one string here and four in the pack.
   *
   * ⚠ TITLE AND HOOK ARE BUILDER PLACEHOLDERS PENDING THE FOUNDER'S PEN, as is every content field
   * in `packs/spliff-donk-gengja/pack.json` except the demonstrations, which are his own transcript
   * of the sketch, verbatim.
   *
   * THE HOOK NAMES THE SITUATION AND NEVER THE CRACK (the crack law, chat-pack §4 via
   * premise-space §5a). It says what is arriving — a cold message, a list, a product with a name
   * and no description — and says nothing about what happens when you ask him what the three
   * things are, or tell him you do not want them. It never names the technology.
   */
  {
    id: 'spliff-donk-gengja',
    packId: 'spliff-donk-gengja',
    title: 'Sölumaðurinn',
    hook: 'He is working down a list of phone numbers from his van, and yours is the next one. He sells spliff, donk og gengja.',
    active: false,
  },
]

/* ───────────────────────────────────────────────────────────────────────────────────────────
 * THE SIXTH SHELF — ASSISTANTS (founder-commissioned 2026-09-02, the night the first one was
 * built: "as a selectable pack on the front page").
 *
 * ⚠ IT REUSES `Conversation`, exactly as DISCOURSES and LEARNING do: a row is a person bound to
 * an arrangement, sold at the size of a row. What differs is the SECTION, and the section is a
 * promise — an assistant is a standing arrangement about YOUR OWN life (your day, your
 * commitments) with a person whose one subject is you. The roster ruling (2026-08-31): a roster
 * of specialists, never a general wrapper. Not comedy, not a method, not a lesson — which is why
 * she could not be filed under any of the three, and a heading that promised COMEDY over her
 * would be the shelf lying.
 * ⚠ THE LABEL IS A BUILDER PLACEHOLDER (`shelf.sectionAssistants` in lib/i18n.tsx) — the
 * founder's pen, in all five languages.
 */
export const ASSISTANTS: Conversation[] = [
  /*
   * ⚠ THE SECRETARY (2026-09-02) — the roster's first member, built as a daily test of the
   * engine's character half at the chair nearest the assistant. The sheet is
   * `packs/the-secretary/sheet.md`; the name is a builder strawman. The hook names the
   * arrangement and never the crack. TITLE AND HOOK ARE THE FOUNDER'S PEN.
   */
  {
    id: 'the-secretary',
    packId: 'the-secretary',
    title: 'The Secretary',
    hook: 'She keeps one person’s diary now, by message. Mornings, what the day is for. Nights, what became of it.',
    active: true,
  },
  /*
   * ⚠ THE STEWARD, COLD (2026-09-13) — the assistant app's first character as a casting line and
   * nothing else, for the founder's manual sitting before the sheet is written
   * (`packs/the-steward/README.md`). Placeholder name; title and hook are the builder's until the
   * founder's pen. Replaced whole by the compiled character.
   */
  {
    id: 'the-steward',
    packId: 'the-steward',
    title: 'The Steward',
    hook: 'He ran a house for thirty-nine years. Bring him anything.',
    active: true,
  },
  /*
   * ⚠ THE TEACHER (2026-09-14) — the assistant app's learning door: a general teacher, maths first
   * (`packs/the-teacher/README.md`). Her sums are the truth desk's, built the same night: the item,
   * the key and a brought equation are the engine's. Not for a minor until the duties spec's order
   * stands (`docs/specs/2026-09-14-the-minors-duties.md` §5). Placeholder name; title and hook are
   * the builder's until the founder's pen.
   */
  {
    id: 'the-teacher',
    packId: 'the-teacher',
    title: 'The Teacher',
    hook: 'Helen Mercer, maths tutor. Homework, a test, or the thing you never got.',
    active: true,
  },
  /*
   * ⚠ THE EXPLAINER (2026-09-15) — the assistant app's concept door, the adult's front door
   * (`packs/the-explainer/README.md`, `docs/specs/2026-09-15-the-explainer.md`): a helpful
   * autodidact who is personally invested in your success. How a thing works, in two lines, then
   * the one question; a number with its date or its place, never from memory; nothing of today's
   * news. A course or an exam is the teacher's. Adults only until the minors' duties stand.
   * Placeholder name; title and hook are the builder's until the founder's pen.
   */
  {
    id: 'the-explainer',
    packId: 'the-explainer',
    title: 'The Explainer',
    hook: 'Nell Ives. Whatever you\'ve been wondering about, and how it actually works.',
    active: true,
  },
  /*
   * ⚠ THE FIXER (2026-09-18) — the household member for the thing that has stopped working
   * (`packs/the-fixer/README.md`, `docs/specs/2026-09-18-the-fixer.md`): the car, the boiler, the
   * sink, the fuse board. A tree walked by symptom, one question at a time, the cheap test before
   * the dear one, the stop line early; understanding after it runs, as a door to the explainer. Cast
   * from the household's own table, not yet probed. Adults only until the minors' duties stand.
   * Placeholder name; title and hook are the builder's until the founder's pen.
   */
  {
    id: 'the-fixer',
    packId: 'the-fixer',
    title: 'The Fixer',
    hook: 'Bill Trow. Tell him what\'s stopped, and what it does when you try it.',
    active: true,
  },
  /*
   * ⚠ THE SPANISH TEACHER (2026-09-21) — Marta Rinaudo as a member of the household, the proving
   * ground for the assistant that knows one person well (`packs/the-spanish-teacher/README.md`,
   * `characters/marta.md`). English carrier as the interim for an Icelandic learner: the founder's
   * rule is the native tongue, once the seat's writing of a small language has been vetted. August's
   * `castellano` row in LEARNING is untouched and stays as the control arm. Not yet probed or sat.
   * Adults only until the minors' duties stand. Placeholder names; title and hook are the builder's
   * until the founder's pen.
   */
  {
    id: 'the-spanish-teacher',
    packId: 'the-spanish-teacher',
    title: 'The Spanish Teacher',
    hook: 'Marta Rinaudo, in Buenos Aires. Thirty years of castellano, one student at a time. She will make you speak.',
    active: true,
  },
]

/* ───────────────────────────────────────────────────────────────────────────────────────────
 * THE THIRD SHELF — DISCOURSES (founder-commissioned 2026-08-26).
 *
 * ⚠ IT REUSES `Conversation` AND THAT IS THE POINT, not a shortcut. A Discourses entry and a Chats
 * entry are the same OBJECT on the page — a name, one line, the whole row a door — because both are
 * one framed exchange with one person, sold at the size of a row. What differs is the SECTION they
 * stand under, and a section is a promise about TONE, never about shape or rendering surface (the
 * rule the CHATS naming carries: `lib/i18n.tsx`, `shelf.sectionChats`). Chats promise something
 * light; Discourses promise something sincere. That is the whole distinction, it lives entirely in
 * which list an entry is in, and giving it a second type would have said the two were built
 * differently when they are not.
 *
 * WHY IT IS ITS OWN LIST AND NOT A FLAG: exactly the reason `Scenario.shelf` was deleted a day
 * after it was added (see the note in the interface above). Grouping by a field on the entry makes
 * the shelf's order a property of the entries; grouping by list makes it a property of the shelf,
 * which is what it actually is. `Selector.tsx` reads all three lists and orders them there.
 *
 * WHAT A DISCOURSE IS: a thinking partner you talk to in order to think better. No end goal, no
 * win, no score, no summary — the reader leaves when they leave. The packs are shaped
 * like `packs/hildur` (a visit, revived per visit) and not like `packs/the-argument` (a round).
 * ⚠ "No clock" was amended 2026-08-27 (the wallet's bell law, `docs/guides/the-wallet.md` §0):
 * every pack authors a natural, lenient, diegetic stopping time — for Ivor, the light going, never
 * an appointment — and the reader can always ask for more. The visit still ends when they leave;
 * it now also ends when the world does.
 * ───────────────────────────────────────────────────────────────────────────────────────────
 */
export const DISCOURSES: Conversation[] = [
  /*
   * ⚠ TITLE AND HOOK ARE BUILDER PLACEHOLDERS PENDING THE FOUNDER'S PEN — THE NAME ABOVE ALL, and
   * every particular in `packs/ivor/pack.json` with it. Nothing here has been through a sitting.
   *
   * ⚠ THE TITLE IS HIS NAME, on the same rule Indriði is: he works for nobody, so there is no role
   * to tell him apart by. A company page is what staff get.
   *
   * THE HOOK SAYS THE PROMISE, WHICH IS THE OPPOSITE OF THE CHATS RULE ABOVE. A Chat card must
   * never print its own gambit, because finding it is the game; a Discourse has no gambit and no
   * game, so withholding what he does would be withholding the entire offer. It still never names
   * the technology, and it never names a method.
   */
  {
    id: 'ivor',
    packId: 'ivor',
    /*
     * ⚠ OFF THE SHELF — IVOR MOVED INSIDE THE ACADEMY (founder-ruled 2026-08-27).
     *
     * He was held here for a few hours, then the better answer arrived: the row was the problem,
     * not the name. A row headed IVOR asks a reader to already know who that is, where a company
     * card sells the offer and lets the person appear on the page behind it — which is exactly how
     * Indriði works behind Fóstbræður.
     *
     * ⚠ `active: false`, NOT DELETED, and not `enterable: false` either. He is not *coming*; he is
     * *elsewhere* — one shelf entry per thing, and his is the Academy's card now
     * (`content/companies.ts`). `/p/ivor` keeps working, as every pack address does.
     */
    active: false,
    title: 'Ivor',
    /*
     * ⚠ KEPT, AND NOT WHAT THE SHELF SHOWS ANY MORE — the Academy's card carries the offer now, and
     * Ivor's own line lives on its roster (`companies.ts`, `people`).
     *
     * ⚠ AND IT SHOULD BE RE-READ BEFORE IT IS EVER USED AGAIN. *"He will not let a word of it stand
     * unopened"* promises examination, and his sheet is explicit that he is not an examiner: he
     * listens, says what he noticed once, and leaves it. The copy had drifted toward the Socratic
     * man the design rules out — which is most likely why Socrates came to mind as a rename at all.
     */
    hook: 'He mends chairs at the end of his garden and he is in no hurry at all. Bring him something you are trying to think through; he will not let a word of it stand unopened.',
  },
  /*
   * ⚠ SOKRATIS — the Academy's second member (2026-08-31; `packs/sokratis/sheet.md` is the source,
   * compiled and probe-verified the same day). BUILT OFF THE SHELF FROM THE FIRST MINUTE, which is
   * the one thing that makes this entry different from Ivor's above: he was never a row that got
   * moved, so `active: false` here is not a demotion but his starting state. The Academy's card
   * (`content/companies.ts`, id `academy`) is his only route from the shelf, and it opened the day
   * he arrived.
   *
   * ⚠ IT EXISTS FOR ONE REASON, AND DELETING IT 404s HIM. `/p/:packId` must resolve to a GRAMMAR
   * before `PackSurface` can pick a house, and `findScenario` reads these lists — an unresolved id
   * is `<Navigate to="/" />`, the silent bounce to the shelf. A roster row's `href` is not a
   * registration; this is. Same reason `castellano` and `islenska` sit in LEARNING with their rows
   * off.
   *
   * ⚠ IN *DISCOURSES* THOUGH HIS CARD IS ON THE *LEARNING* SHELF, and the mismatch is deliberate
   * rather than a filing slip. These lists are promises about TONE (see the header above): his
   * constitution is Discourses, sincere lane, and that is what this list means. Which SHELF he is
   * reached from is the Academy card's business, and the Academy has sat under LEARNING since
   * 2026-08-28 while keeping the sincere register. `findScenario` searches the row shelves as one,
   * so the choice costs nothing mechanically and keeps the constitution readable — he belongs
   * beside Ivor.
   *
   * ⚠ TITLE AND HOOK ARE BUILDER-AUTHORED PENDING THE FOUNDER'S PEN, as is every content field in
   * the pack. THE HOOK RENDERS NOWHERE TODAY (`active: false`), and is written to the Discourses
   * rule anyway: it says the promise, because a Discourse has no gambit to spoil; it never names
   * the technology; and it never names a method — no "Socratic", no "elenchus".
   *
   * ⚠ REWRITTEN 2026-08-31 with the roster ruling: the hook carried the retired BELIEF door
   * ("bring one thing you believe… until you know whether it is yours"). His door is now STUCK
   * AND CAN'T MOVE, and the hook leads with the arrival state like both roster lines do.
   *
   * ⚠ *THE EXAMINED LIFE* IS RETIRED (founder-ruled 2026-08-31, same sitting). It was the
   * offering's name in the design and it rendered NOWHERE — not here, not in the strap, not in
   * either roster line. Retired rather than rewritten, for three reasons worth keeping so nobody
   * mints it again: the shelf already names at the two levels that work (the house, then the
   * person), and a door-led line does the offering's job better than a label; the name is
   * method-language, so it quietly smuggled the retired belief frame back onto the shelf; and
   * the recognition it was carrying is already carried diegetically by THE SOCRATES CHAIR, which
   * is a real kind of thing an institution has. There is no offering name, by decision.
   */
  {
    id: 'sokratis',
    packId: 'sokratis',
    active: false,
    title: 'Sokratis',
    hook: 'Bring the thing you have gone back and forth on for months. He asks until you can say what you actually think, and holds no position of his own.',
  },
]

/* ───────────────────────────────────────────────────────────────────────────────────────────
 * THE FOURTH SHELF — LEARNING (founder-commissioned 2026-08-28: "wire up for product. we need
 * to start off with the language learning pack. it should be on the 'learning' shelf").
 *
 * The Learning avenue's shelf (`docs/strategy/2026-08-28-the-learning-avenue.md`, direction
 * ratified). It reuses `Conversation` on the same rule DISCOURSES does: the entry is the same
 * OBJECT on the page — a name, one line, the whole row a door — and the section heading carries
 * the entire distinction. What LEARNING promises is its constitution: SINCERE AND OVERT — the
 * one shelf where the purpose may be said aloud (a teacher is a teacher, the student came to
 * learn), while the machinery stays worn, never narrated. Claims cap: a teacher, tuition —
 * never certification, never outcomes promised.
 *
 * THE HOOK SAYS THE OFFER, as a Discourse's does — an overt shelf withholding what the teacher
 * does would be withholding the product. It still never names the technology and never names a
 * method.
 *
 * ⚠ THE FUTURE SHAPE IS A SCHOOL, NOT A LIST OF TEACHERS (avenue §4.2: a language-school
 * company page — you pick your language from a list, each language opens its teacher). One
 * teacher is a row, on the same rule one man is a row (Indriði's); the school card arrives when
 * there are languages plural.
 * ─────────────────────────────────────────────────────────────────────────────────────────── */
export const LEARNING: Conversation[] = [
  /*
   * ⚠ MARTA — the LEARNING shelf's first character and the avenue's POC (packs/castellano/sheet.md
   * is the character's source; the pack compiled and probe-verified 2026-08-28). TITLE AND HOOK
   * ARE BUILDER PLACEHOLDERS PENDING THE FOUNDER'S PEN, as is every content field in the pack.
   *
   * ⚠ OFF THE SHELF, INTO `/c/languages` (founder-ruled 2026-08-28, within the hour of the row
   * going live: *"the Learning shelf has a specific language-learning page… you pick a language
   * first. 'Marta' just happens to be the teacher"*). Same mechanics as Indriði behind
   * Fóstbræður: `active: false` removes the row, `findScenario` keeps `/p/castellano` opening
   * exactly as it did, and the school page (`content/companies.ts`, id `languages`) is the way
   * in — the CASTELLANO row there names her incidentally, which is the point: you choose the
   * language; she is who it comes with.
   */
  {
    id: 'castellano',
    packId: 'castellano',
    title: 'Marta',
    hook: 'Thirty years of castellano in Buenos Aires; one student at a time. Bring your reasons — she will make you speak.',
    active: false,
  },
  /*
   * ⚠ EWA — the second LEARNING character (2026-08-29; packs/islenska/sheet.md is the source,
   * compiled and probe-verified the same day). Same mechanics as Marta: `active: false` keeps
   * the row off the shelf, `findScenario` keeps `/p/islenska` opening, and the school page's
   * ÍSLENSKA row is the way in — you choose the language; she is who it comes with. TITLE AND
   * HOOK ARE BUILDER PLACEHOLDERS PENDING THE FOUNDER'S PEN; the pl row line is the Polish
   * tester's to gate.
   */
  {
    id: 'islenska',
    packId: 'islenska',
    title: 'Ewa',
    hook: 'Icelandic from a teacher who crossed the same water: Reykjavík since 2004, one student at a time.',
    active: false,
  },
]

/**
 * A CONVERSATION, AS `PackSurface` NEEDS TO SEE IT (pack-per-conversation, 2026-08-25).
 *
 * `/p/:packId` must resolve to a GRAMMAR before a house can be picked, and `findScenario` is
 * where that happens — which is why `characters-poc` sat in `SCENARIOS` with `active: false`
 * after it had stopped being a card. Four packs would have been four such entries. Instead the
 * grammar is taken from the row, because a conversation runs in the messaging house BY
 * DEFINITION: it is the Conversations format, the surface IS the whole of the offer, and there is
 * nothing per-pack left for an entry to declare.
 *
 * ⚠ THE CARD FIELDS RENDER NOWHERE. `Scenario` demands them because a card needs them, and a
 * conversation has no card: `PackSurface` reads `id`, `grammar`, `theme` and `doors`, and nothing
 * else. They carry the ROW'S OWN words rather than invented copy, so that the day a conversation
 * gets a page of its own it says what the row said.
 */
const asScenario = (c: Conversation): Scenario => ({
  id: c.packId,
  title: c.title,
  grammar: 'messaging',
  /* The accent Four Doors was given, kept rather than re-invented per row. Unrendered today. */
  accent: '#b06a4f',
  blurb: c.hook,
  tags: [],
})

/** What the shelf shows. */
export const activeScenarios = () => SCENARIOS.filter((s) => s.active)

/** What the fifth shelf shows. */
export const activeGames = () => GAMES.filter((g) => g.active)

/** What the second shelf shows. */
export const activeConversations = () => CONVERSATIONS.filter((c) => c.active)

/** What the third shelf shows. */
export const activeDiscourses = () => DISCOURSES.filter((d) => d.active)

/** What the fourth shelf shows. */
export const activeLearning = () => LEARNING.filter((l) => l.active)

/** What the sixth shelf shows. */
export const activeAssistants = () => ASSISTANTS.filter((a) => a.active)

/**
 * A pack id → the entry that says which house renders it.
 *
 * Three lists, in order: the WORLDS shelf first, then the row shelves — CONVERSATIONS and
 * DISCOURSES — which resolve through `asScenario` at the foot of this file rather than by holding
 * a dead card up here. All are searched regardless of `active` — being off the shelf has never
 * meant the address stops working, and a row a reader was linked to must open whether or not it is
 * currently listed.
 *
 * ⚠ THE ROW SHELVES ARE SEARCHED AS ONE, deliberately: a row resolves to the messaging house
 * whichever section it stands under, because the section is a promise about tone and the house is
 * a fact about shape. If a Discourse ever needed a different house, that is the day this splits.
 */
export const findScenario = (id: string | undefined): Scenario | undefined => {
  /*
   * ⚠ BOTH CARD LISTS, ALWAYS. GAMES was split out of SCENARIOS on 2026-09-01 and a card's address
   * has nothing to do with which heading it stands under — searching only one list here is exactly
   * how `/p/silfurberg` would 404 the day it moved shelf.
   */
  const world = [...SCENARIOS, ...GAMES].find((s) => s.id === id)
  if (world) return world
  const row = [...CONVERSATIONS, ...DISCOURSES, ...LEARNING, ...ASSISTANTS].find((c) => c.packId === id)
  return row && asScenario(row)
}

/**
 * Resolve a PACK id back to its scenario — the scenario's own id, or any of its
 * doors' compiled packs.
 *
 * A door compiles to its own pack (`silfurberg-managed`, …), so once doors two
 * to four exist a surface loaded by door would otherwise find no scenario and
 * lose its title, role and blurb. The ground is one scenario however many
 * bottoms it has.
 */
export const findScenarioByPack = (packId: string | undefined) =>
  // Both card lists, for the reason `findScenario` states — and this one carries the doors, which
  // are Silfurberg's, which is the card that moved.
  [...SCENARIOS, ...GAMES].find(
    (s) => s.id === packId || s.doors?.some((d) => d.packId === packId),
  )

export const roleOf = (s: Scenario) => s.role ?? DEFAULT_ROLE[s.grammar]

/**
 * The card's copy in one language, falling back FIELD BY FIELD to the English base.
 *
 * `role` resolves through `roleOf` so a pack without its own role still gets the grammar's
 * default — and a translated override, when one exists, wins over both.
 */
export function copyFor(s: Scenario, locale: Locale) {
  const over = locale === 'en' ? undefined : s.copy?.[locale]
  return {
    title: over?.title ?? s.title,
    role: over?.role ?? roleOf(s),
    blurb: over?.blurb ?? s.blurb,
    tags: over?.tags ?? s.tags,
  }
}

/** The row's copy in one language, falling back FIELD BY FIELD to the English base. */
export function copyForConversation(c: Conversation, locale: Locale): Required<ConversationCopy> {
  const over = locale === 'en' ? undefined : c.copy?.[locale]
  return {
    title: over?.title ?? c.title,
    hook: over?.hook ?? c.hook,
  }
}

/** One route for every pack; the grammar decides the component, not the URL. */
export const hrefFor = (s: Scenario) => `/p/${s.id}`

/**
 * A CONVERSATION'S OWN ADDRESS — the pack, and nothing else in it.
 *
 * ⚠ THE COLLAPSE LANDED (2026-08-25). This carried `?door=<personaId>` for one day, because four
 * conversations shared one pack and the row had to say which of the four it was. Under
 * pack-per-conversation the pack IS the conversation: one persona in it, one door off the wire,
 * and `surfaces/messaging` enters it without being told which. The address is the pack.
 *
 * `?door=` itself is untouched and still generic — `PackSurface` forwards it, the surface matches
 * it against the wire's doors — so a link written yesterday against a pack that still has several
 * personas keeps working. Nothing generates one any more.
 */
export const hrefForConversation = (c: Conversation) => `/p/${c.packId}`
